import {inject, Injectable} from '@angular/core';
import {HttpContext, HttpParams} from '@angular/common/http';
import {Params} from '@angular/router';
import {map, Observable} from 'rxjs';
import {BorrowingHistoryRecord, BulkActionPartialCounts, Checkout, CheckoutClaim, FileClaimValue} from './checkouts.types';
import {DateOnlyIsoString, ListData} from '@aviary/store/common/common.types';
import {buildQueryParamsFrom} from '@aviary/store/common/list-data.helpers';
import {ApiService} from '@aviary/services/api.service';
import {BYPASS_400_ERROR} from '@aviary/store/http-loading/http-loading.types';
import {isPopulatedArray} from '@aviary/store/common/typing.helpers';
import {MxResponseBaseForBulkAction} from '@aviary/store/common/response-base.types';
import {
  contextWithErrorHandlerForFileClaim,
  contextWithErrorHandlerForLoadBorrowingHistory,
  contextWithErrorHandlerForRenew
} from '@store/checkouts/checkouts.handlers';

@Injectable({providedIn: 'root'})
export class CheckoutsService {

  private readonly apiService: ApiService = inject(ApiService);

  public loadCheckedOut(queryParams: Params): Observable<ListData<Checkout>> {
    const endpoint: string = 'patrons/v2/patrons/checkouts';
    const params: HttpParams = buildQueryParamsFrom(queryParams);

    return this.apiService.getListData(endpoint, {params});
  }

  public loadCheckoutClaims(queryParams: Params): Observable<ListData<CheckoutClaim>> {
    const endpoint: string = 'patrons/v2/patrons/checkouts/claims';
    const params: HttpParams = buildQueryParamsFrom(queryParams);

    return this.apiService.getListData(endpoint, {httpOptions: {params}, includePaginationInfo: false});
  }

  public loadNextCheckedOutDueDate(): Observable<DateOnlyIsoString | undefined> {
    const endpoint: string = 'patrons/v2/patrons/checkouts';
    const params: HttpParams = buildQueryParamsFrom({sort: 'dueDate', pageSize: 1});

    return this.apiService.getOptionalSingleObject<Checkout>(endpoint, {params}).pipe(
      map(checkout => checkout?.dueDate)
    );
  }

  public loadBorrowingHistory(queryParams: Params): Observable<ListData<BorrowingHistoryRecord>> {
    const endpoint: string = 'patrons/v1/checkouts/reading-history';
    const params: HttpParams = buildQueryParamsFrom(queryParams);

    return this.apiService.getListData(endpoint, {params, context: contextWithErrorHandlerForLoadBorrowingHistory()});
  }

  public renew(checkoutId: number, itemTitle: string): Observable<Checkout> {
    const endpoint: string = `patrons/v2/checkouts/${checkoutId}/renew`;

    return this.apiService.patchAndReturnSingleObject(endpoint, {}, {context: contextWithErrorHandlerForRenew(itemTitle)});
  }

  public renewAll(): Observable<BulkActionPartialCounts> {
    const endpoint: string = `patrons/v2/checkouts/renew-all`;
    const context: HttpContext = new HttpContext().set(BYPASS_400_ERROR, true);
    return this.apiService.patch<MxResponseBaseForBulkAction<Checkout>, object>(endpoint, {}, {context}).pipe(
      map((response) => ({
        successCount: isPopulatedArray(response.objects) ? response.objects.length : 0,
        failureCount: isPopulatedArray(response.partialFailures) ? response.partialFailures.length : 0
      }))
    );
  }

  public fileClaim(value: FileClaimValue): Observable<void> {
    const endpoint: string = `patrons/v5/patrons/checkouts/claims`;

    return this.apiService.post(endpoint, value, {context: contextWithErrorHandlerForFileClaim()});
  }

}
